import { ShiftBlock, Store, timeRangeString } from '@nextforce/api'
import { makeStyles, Text } from '@rneui/themed'
import { filter, findIndex, times } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import {
  Col,
  Row,
  Rows,
  Table,
  TableWrapper,
} from 'react-native-table-component'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { Cell, TableHeader } from './types'
import { getWeekDaysShort } from './utils'

const DAYS_IN_WEEK = 7

interface NonAvailabilityGridProps {
  store?: Store
  cells?: Cell[]
  disableSelection?: boolean
  onCellPress?(cellData: Cell[]): void
}

export function NonAvailabilityGrid({
  store,
  cells: initialSelectedCells,
  disableSelection,
  onCellPress,
}: NonAvailabilityGridProps) {
  const styles = useStyles()

  const shiftBlocks: ShiftBlock[] = useMemo(() => {
    return store?.shiftBlocks?.filter(block => block.active) ?? []
  }, [store?.shiftBlocks])

  const [header, setHeader] = useState<TableHeader[]>([])

  useEffect(() => {
    const tableHead = shiftBlocks.map(shiftBlock => ({
      name: shiftBlock.name,
      time: timeRangeString(shiftBlock.startTime, shiftBlock.endTime),
    }))

    tableHead.unshift({ name: '', time: '' })

    setHeader(tableHead)
  }, [shiftBlocks])

  const [weekDays, __] = useState<string[]>(getWeekDaysShort())

  const [cells, setCells] = useState<Cell[][]>()

  useEffect(() => {
    const cellData = times(DAYS_IN_WEEK, row =>
      times(shiftBlocks.length, col => ({
        shiftBlockId: String(shiftBlocks[col].id),
        dayOfWeek: String(row),
      }))
    )

    setCells(cellData)
  }, [shiftBlocks])

  const [selectedCells, setSelectedCells] = useState<Cell[]>(
    initialSelectedCells ?? []
  )

  useEffect(() => {
    setSelectedCells(initialSelectedCells ?? [])
  }, [initialSelectedCells])

  const handleCellPress = (cellData: Cell) => {
    setSelectedCells(prev => {
      const cellIndexInSelection = findIndex(prev, {
        shiftBlockId: cellData.shiftBlockId,
        dayOfWeek: cellData.dayOfWeek,
      })

      const updatedSelection =
        cellIndexInSelection > -1
          ? filter(prev, (_, index) => index !== cellIndexInSelection)
          : [...prev, cellData]

      onCellPress?.(updatedSelection)

      return updatedSelection
    })
  }

  const getCellStyle = (cellData: Cell) => {
    return selectedCells.some(
      cell =>
        cell.dayOfWeek === cellData.dayOfWeek &&
        cell.shiftBlockId === cellData.shiftBlockId
    )
      ? { backgroundColor: '#87898c' }
      : {}
  }

  if (!shiftBlocks.length) {
    return null
  }

  return (
    <Table borderStyle={{ borderWidth: 1 }} style={styles.container}>
      <Header header={header} />
      <TableWrapper style={styles.wrapper}>
        <Col data={weekDays} style={styles.col} textStyle={styles.text} />

        <Rows
          data={cells?.map((rows, _) =>
            rows.map((cellData, colIndex) => (
              <PressableOpacity
                disabled={disableSelection}
                key={String(colIndex)}
                style={[{ flex: 1 }, getCellStyle(cellData)]}
                onPress={() => handleCellPress(cellData)}
              ></PressableOpacity>
            ))
          )}
          flexArr={generateFlexArray(shiftBlocks)}
          style={styles.row}
          textStyle={styles.text}
        />
      </TableWrapper>
    </Table>
  )
}

interface TableHeaderProps {
  header: TableHeader[]
}

function Header({ header, ...props }: TableHeaderProps) {
  const styles = useStyles()

  const renderTableHead = (shiftBlock: TableHeader) => {
    return (
      <View>
        <Text style={styles.time}>{shiftBlock.time}</Text>
      </View>
    )
  }
  return (
    <Row
      data={header.map(renderTableHead)}
      style={StyleSheet.flatten(styles.head)}
      {...props}
    />
  )
}

function generateFlexArray(shiftBlock: ShiftBlock[]): number[] {
  return Array(shiftBlock.length).fill(1)
}

const useStyles = makeStyles(theme => ({
  container: {
    backgroundColor: 'white',
  },
  head: {
    height: 70,
    backgroundColor: theme.mode === 'dark' ? '#88C0D0' : '#f1f8ff',
  },
  wrapper: {
    flexDirection: 'row',
  },
  col: {
    flex: 1,
    backgroundColor: theme.mode === 'dark' ? '#88C0D0' : '#f1f8ff',
  },
  row: {
    height: 58,
    borderWidth: 0,
  },
  text: {
    textAlign: 'center',
    flexWrap: 'wrap',
    fontSize: 12,
    margin: 2,
    color: 'black',
  },
  time: {
    textAlign: 'center',
    flexWrap: 'wrap',
    margin: 2,
    fontSize: 9,
    color: 'black',
  },
}))
